
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Trash2, Eye, Edit, MapPin, ExternalLink } from 'lucide-react';

interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string | null;
  featured_image: string | null;
  slug: string;
  author: string;
  published_date: string | null;
  status: 'draft' | 'published';
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string[] | null;
  categories: string[] | null;
  tags: string[] | null;
  created_at: string | null;
  updated_at: string | null;
}

interface PostsTableProps {
  posts: BlogPost[];
  selectedDrafts: Set<string>;
  onSelectDraft: (postId: string, checked: boolean) => void;
  onToggleStatus: (id: string, currentStatus: 'draft' | 'published') => void;
  onDeletePost: (id: string) => void;
  extractLocationInfo: (post: BlogPost) => string | null;
  generateLocationSlug: (post: BlogPost, locationName: string | null) => string;
}

const PostsTable: React.FC<PostsTableProps> = ({
  posts,
  selectedDrafts,
  onSelectDraft,
  onToggleStatus,
  onDeletePost,
  extractLocationInfo,
  generateLocationSlug
}) => {
  const getPostType = (post: BlogPost) => {
    const locationInfo = extractLocationInfo(post);
    return locationInfo ? 'location' : 'template';
  };

  const generatePublicUrl = (post: BlogPost) => {
    // Use the post's slug directly for the public URL
    return `/blog/${post.slug}`;
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-12">Select</TableHead>
          <TableHead>Title</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Author</TableHead>
          <TableHead>Date</TableHead>
          <TableHead>Public URL</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {posts.map(post => {
          const locationInfo = extractLocationInfo(post);
          const postType = getPostType(post);
          const publicUrl = generatePublicUrl(post);
          
          return (
            <TableRow key={post.id}>
              <TableCell>
                {post.status === 'draft' && (
                  <Checkbox
                    checked={selectedDrafts.has(post.id)}
                    onCheckedChange={(checked) => onSelectDraft(post.id, checked as boolean)}
                  />
                )}
              </TableCell>
              <TableCell className="font-medium">
                <div className="max-w-xs truncate" title={post.title}>
                  {post.title}
                </div>
              </TableCell>
              <TableCell>
                <Badge variant={post.status === 'published' ? 'default' : 'outline'}>
                  {post.status === 'published' ? 'Published' : 'Draft'}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {postType === 'location' && locationInfo ? (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {locationInfo}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-purple-600 border-purple-200">
                      Template
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>{post.author}</TableCell>
              <TableCell>
                <span className="text-sm text-muted-foreground">
                  {post.published_date ? new Date(post.published_date).toLocaleDateString() : 'No date'}
                </span>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <div className="text-xs text-muted-foreground max-w-xs truncate" title={publicUrl}>
                    {publicUrl}
                  </div>
                  {post.status === 'published' && (
                    <Button variant="ghost" size="sm" asChild>
                      <Link to={publicUrl} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    </Button>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/post-preview/${post.id}`}>
                      <Eye className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/create-post?edit=${post.id}`}>
                      <Edit className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => onToggleStatus(post.id, post.status)}
                  >
                    {post.status === 'published' ? 'Unpublish' : 'Publish'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => onDeletePost(post.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default PostsTable;
